import React, { useState } from 'react';
import { FlaskConical, Rocket, Trash2 } from 'lucide-react';
import { useBotTestMode } from '../hooks/useBotTestMode';
import { MAX_TEST_NUMBERS, prettyNumber } from '../utils/botTestMode';

const COUNTRIES = [['57', '🇨🇴 +57'], ['52', '🇲🇽 +52'], ['1', '🇺🇸 +1'], ['54', '🇦🇷 +54'], ['', '🌍 Otro']];
const inputCls = 'bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none placeholder:text-seryi-text';

// Tarjeta de Configuración: el dueño prueba el bot con sus números y, cuando queda afinado, lo enciende para todos.
export default function BotTestModeCard() {
  const { business, mode, numbers, saving, addNumber, removeNumber, publish, backToTest } = useBotTestMode();
  const [raw, setRaw] = useState('');
  const [prefix, setPrefix] = useState('57');
  if (!business) return null;

  const inTest = mode === 'prueba';
  const paused = mode === 'pausado';
  const add = async () => { if (await addNumber(raw, prefix)) setRaw(''); };

  return (
    <div className={`border p-5 rounded-3xl space-y-4 ${inTest ? 'bg-amber-500/5 border-amber-500/40' : 'bg-seryi-bg border-seryi-bg-light'}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${inTest ? 'bg-amber-500/15 text-amber-300' : 'bg-seryi-green/10 text-seryi-green'}`}>
            {inTest ? <FlaskConical size={18} /> : <Rocket size={18} />}
          </div>
          <div>
            <h4 className="font-bold text-sm text-seryi-heading flex items-center gap-2 flex-wrap">
              MODO PRUEBA DEL BOT
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${inTest ? 'text-amber-300 border-amber-500/40 bg-amber-500/10' : 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10'}`}>
                {inTest ? 'En pruebas' : 'Encendido para todos'}
              </span>
            </h4>
            <p className="text-[11px] text-seryi-heading mt-0.5 max-w-xl">
              {inTest
                ? 'El bot solo le responde a los números de esta lista. Los demás clientes que escriban no reciben respuesta del bot: los atiendes tú. Afina la identidad, los precios y las respuestas, y cuando quede listo enciéndelo para todos.'
                : 'El bot responde a todos tus clientes. Si quieres volver a afinarlo sin que los clientes lo vean, vuelve a modo prueba.'}
            </p>
          </div>
        </div>
        {inTest ? (
          <button type="button" disabled={saving} onClick={() => { if (window.confirm('Desde ahora el bot responderá a TODOS los clientes que escriban a tu WhatsApp. ¿Encenderlo para todos?')) publish(); }}
            className="px-4 py-2.5 rounded-xl font-bold text-xs shrink-0 bg-emerald-600 hover:bg-emerald-700 text-seryi-heading shadow-md disabled:opacity-50">Encender para todos</button>
        ) : (
          <button type="button" disabled={saving} onClick={() => { if (window.confirm('Mientras esté en modo prueba el bot NO responde a tus clientes, solo a tus números de prueba. ¿Volver a modo prueba?')) backToTest(); }}
            className="px-4 py-2.5 rounded-xl font-bold text-xs shrink-0 border border-seryi-bg-light text-seryi-heading hover:bg-seryi-bg-light/30 disabled:opacity-50">Volver a modo prueba</button>
        )}
      </div>

      {paused && <p className="text-[11px] text-amber-300">El bot está en pausa para todos los chats (arriba): mientras siga en pausa no responde a nadie, ni en modo prueba ni encendido.</p>}

      {inTest && (
        <div className="space-y-3">
          <p className="text-[10px] uppercase font-bold text-seryi-text">Números de prueba ({numbers.length} de {MAX_TEST_NUMBERS})</p>
          {numbers.length === 0 ? (
            <p className="text-[11px] text-amber-300">Aún no hay números de prueba: mientras la lista esté vacía el bot no responde a nadie. Agrega tu celular para probarlo.</p>
          ) : (
            <ul className="space-y-1.5">
              {numbers.map((n) => (
                <li key={n} className="flex items-center justify-between gap-3 bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2">
                  <span className="text-xs font-mono text-seryi-heading">{prettyNumber(n)}</span>
                  <button type="button" disabled={saving} onClick={() => removeNumber(n)} aria-label={`Quitar ${prettyNumber(n)}`} className="text-seryi-text hover:text-rose-400 disabled:opacity-50"><Trash2 size={14} /></button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <select aria-label="Indicativo del país" value={prefix} onChange={(e) => setPrefix(e.target.value)} className={`${inputCls} font-mono shrink-0`}>
              {COUNTRIES.map(([v, l]) => <option key={l} value={v}>{l}</option>)}
            </select>
            <input type="text" inputMode="tel" aria-label="Número de prueba" placeholder="Tu celular, ej. 3016867636" value={raw} onChange={(e) => setRaw(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') add(); }} className={`${inputCls} flex-1`} />
            <button type="button" disabled={saving || !raw.trim() || numbers.length >= MAX_TEST_NUMBERS} onClick={add}
              className="bg-seryi-green text-seryi-bg-dark text-xs font-extrabold px-4 py-2.5 rounded-xl disabled:opacity-50">Agregar número</button>
          </div>
          <p className="text-[10px] text-seryi-text">Para probar, escríbele al WhatsApp del negocio desde uno de estos números. Si tu WhatsApp aparece en Clientes solo como «ID interno» (sin número), ábrelo allí, ponle tu teléfono y vuelve a escribir.</p>
        </div>
      )}
    </div>
  );
}
